// @ts-nocheck
import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF, MeshTransmissionMaterial } from '@react-three/drei';
import { Color } from 'three';

// Resolve local GLB asset path via bundler
const CIRCLE_TEXT_URL = new URL('./export/LerenLevenBand.THREEJS.glb', import.meta.url).href;

type CircularBannerProps = {
  position?: [number, number, number];
  y?: number;
  tilt?: number;
  scale?: number | [number, number, number];
  isPlaying?: boolean;
};

export default function CircularBanner({
  position,
  y,
  tilt = -0.12,
  scale,
  isPlaying = false,
}: CircularBannerProps) {
  const groupRef = useRef<any>(null);
  const { nodes, materials } = useGLTF(CIRCLE_TEXT_URL) as any;
  const baseSpeed = 0.5; // radians per second (original)
  const speedRef = useRef(isPlaying ? baseSpeed : baseSpeed * 0.5);

  useFrame((_, delta) => {
    const targetSpeed = isPlaying ? baseSpeed : baseSpeed * 0.5;
    // Exponential smoothing toward target speed (~1s time constant)
    const lambda = 1 - Math.exp(-delta / 1.0);
    speedRef.current += (targetSpeed - speedRef.current) * lambda;
    if (groupRef.current) {
      groupRef.current.rotation.y -= speedRef.current * delta;
    }
  });

  const effectivePosition = useMemo<[number, number, number]>(() => {
    if (position) return position;
    return [0, typeof y === 'number' ? y : 0, 0];
  }, [position, y]);

  const effectiveScale = useMemo<[number, number, number]>(() => {
    if (Array.isArray(scale)) return scale as [number, number, number];
    if (typeof scale === 'number') return [scale, scale, scale];
    return [1, 1, 1];
  }, [scale]);

  return (
    <group
      ref={groupRef}
      dispose={null}
      position={effectivePosition}
      rotation={[tilt, 0, 0]}
      scale={effectiveScale}
    >
      <mesh castShadow receiveShadow geometry={nodes?.Torus?.geometry}>
        <MeshTransmissionMaterial
          background={new Color('#000000')}
          meshPhysicalMaterial={false}
          transmissionSampler={false}
          backside={false}
          samples={10}
          resolution={2048}
          transmission={1}
          roughness={0}
          thickness={0.9}
          ior={1.068}
          chromaticAberration={0}
          anisotropy={0}
          distortion={0.5}
          distortionScale={0.5}
          temporalDistortion={0.5}
          clearcoat={1}
          attenuationDistance={0.5}
          attenuationColor={'#ffffff'}
          color={'#b4a6ff'}
        />
      </mesh>

      <mesh
        castShadow
        receiveShadow
        geometry={nodes?.Text?.geometry}
        material={materials?.['Material.007']}
        position={[0, -0.003, 0]}
        rotation={[Math.PI / 2, 0, -Math.PI]}
        scale={1}
      />
    </group>
  );
}

useGLTF.preload(CIRCLE_TEXT_URL);


